import invert from 'lodash/invert';
import pick from 'lodash/pick';

import {
  BOOLEAN_SEARCH_PARTS,
  CASTING_TOOL_EXPRESSION_PREFIX,
  CASTING_TOOL_SLUGS,
  CONTRIBUTORS,
} from 'constants/index';

import { splitBooleanSearchQuery } from 'utils/globals/filters';

export const getCountriesFromFilters = options =>
  Array.from(
    options?.reduce((acc, { entity }) => {
      if (entity?.id) {
        acc.add(entity.id);
      }

      return acc;
    }, new Set()),
  );

export const getBooleanExpressionPrefix = ({ key, value }) => {
  const matchKey = key === CASTING_TOOL_SLUGS.WORK ? key : value?.type;
  const prefix = CASTING_TOOL_EXPRESSION_PREFIX[matchKey] || '';
  return prefix;
};

export const getExpressionParts = expression => {
  const parts = expression?.split(':');

  const filters = parts?.reduce((acc, part) => {
    const [prefix, ...remaining] = part || '';
    const value = remaining?.join('');
    const prefixSlugsMapping = invert(CASTING_TOOL_EXPRESSION_PREFIX);

    const key = prefixSlugsMapping[prefix];

    if ([CASTING_TOOL_SLUGS.WORK_ROLE, CASTING_TOOL_SLUGS.PROFESSION, CASTING_TOOL_SLUGS.ENSEMBLE].includes(key)) {
      acc[CASTING_TOOL_SLUGS.WORK_ROLE] = {
        type: key,
        id: Number.parseInt(value, 10),
      };
    } else if (CASTING_TOOL_SLUGS.WORK_ROLE_REFERENCE === key) {
      acc[CASTING_TOOL_SLUGS.WORK_ROLE] = {
        type: CASTING_TOOL_SLUGS.WORK_ROLE, // NOTE: Do not change else option will not be pre selected on filters
        id: Number.parseInt(value, 10),
      };
    } else if (CASTING_TOOL_SLUGS.COUNTRY === key) {
      acc[key] = value?.split(',')?.map(id => Number.parseInt(id, 10));
    } else if (key) {
      acc[key] = Number.parseInt(value, 10);
    }

    return acc;
  }, {});

  return filters;
};

const cleanedExpression = expression => {
  if (!expression || typeof expression !== 'string') {
    return '';
  }

  const withRestMatch = expression.match(/^!(w\d+:(q|r)\d+);?\((.*)\)$/);

  if (withRestMatch) {
    const firstPart = withRestMatch[1];
    const rest = withRestMatch[3];
    return `${firstPart};${rest}`;
  }

  const singlePartMatch = expression.match(/^!(w\d+:(q|r)\d+);?$/);

  if (singlePartMatch) {
    return `${singlePartMatch[1]}`;
  }

  return expression;
};

export const getBooleanSearchQuery = expression => {
  const tmpExpression = cleanedExpression(expression);

  return splitBooleanSearchQuery(tmpExpression)?.map(search => {
    if (search?.type === BOOLEAN_SEARCH_PARTS.QUERY) {
      return {
        type: BOOLEAN_SEARCH_PARTS.QUERY,
        value: getExpressionParts(search?.value),
      };
    }

    return search;
  });
};

export const convertRoleDebutExpression = expression => {
  if (!expression) return '';

  const cleaned = expression.startsWith('!') ? expression.slice(1) : expression;
  const match = cleaned.match(/^(w\d+:(q|r)\d+)/);
  if (!match) return '';

  const part = match[1];
  const rest = cleaned.slice(match[0].length);
  const trimmedRest = rest.replace(/^([;|])/, '');

  return `!${part}${trimmedRest ? `;(${trimmedRest})` : ''}`;
};

export const undoRoleDebutExpression = expression => {
  if (!expression) return '';
  if (!expression.startsWith('!')) return expression;

  const cleaned = expression.slice(1); // remove leading !
  const match = cleaned.match(/^(w\d+:(q|r)\d+)(?:;\((.*)\))?$/);

  if (!match) return expression;
  const mainPart = match[1];
  const rest = match[3];

  return rest ? `${mainPart};${rest}` : mainPart;
};

export const getBooleanExpressionString = (booleanQuery, isRoleDebut = false) => {
  const expression = booleanQuery
    ?.map(({ type, value: queryValue }) => {
      if (type === BOOLEAN_SEARCH_PARTS.OPERATOR) {
        return queryValue;
      }

      const query = Object.keys(pick(queryValue, [CASTING_TOOL_SLUGS.WORK, CASTING_TOOL_SLUGS.WORK_ROLE]))
        .map(key => {
          const keyValue = queryValue?.[key];

          if (keyValue) {
            const id = key === CASTING_TOOL_SLUGS.WORK ? keyValue : keyValue?.id || keyValue?.slug;

            const prefix = getBooleanExpressionPrefix({ key, value: keyValue });

            if (prefix) {
              return `${prefix}${id}`;
            }
          }

          return null;
        })
        .filter(Boolean)
        .join(':');

      return query;
    })
    .join('');

  if (isRoleDebut && expression.includes(':')) {
    return convertRoleDebutExpression(expression);
  }

  return expression;
};

export const findCreditGroupByRole = (data, role) => {
  if (!data || !role) return null;

  return data.reduce((acc, item) => {
    if (acc) return acc;

    if (item?.slugType === role?.type) {
      const matchingMember = item?.data?.find(member => member?.id === role?.id);
      return matchingMember?.id ? { ...item, role: matchingMember } : acc;
    }

    return acc;
  }, null);
};

export const cleanBooleanQuery = booleanQuery => {
  const cleaned = [];

  for (let i = 0; i < booleanQuery.length; i += 1) {
    const current = booleanQuery[i];

    if (current.type === BOOLEAN_SEARCH_PARTS.QUERY && !current.value.work_id && current.value.work_id !== 0) {
      if (cleaned.length > 0 && cleaned[cleaned.length - 1].type === BOOLEAN_SEARCH_PARTS.OPERATOR) {
        cleaned.pop();
      }
    }

    cleaned.push(current);
  }

  return cleaned;
};

export const serializeRoleOverlapExpression = (booleanQuery = []) => {
  const groups = booleanQuery.reduce(
    (acc, item, index) => {
      if (item.type === BOOLEAN_SEARCH_PARTS.QUERY) {
        const { type, id } = item.value.work_role_id || {};
        const prefix = CASTING_TOOL_EXPRESSION_PREFIX[type];
        const workId = item.value.work_id;
        const workPrefix = CASTING_TOOL_EXPRESSION_PREFIX[CASTING_TOOL_SLUGS.WORK];
        const workIdStr = workId ? `${workPrefix}${workId}` : '';
        if (type && id) {
          acc[type] += `${workIdStr}:${prefix}${id}`;
        }
      } else if (item.type === BOOLEAN_SEARCH_PARTS.OPERATOR) {
        const prev = booleanQuery[index - 1];
        const prevType = prev?.value?.work_role_id?.type;

        if (prevType && acc[prevType] !== undefined) {
          acc[prevType] += item.value === ';' ? ',' : '~';
        }
      }

      return acc;
    },
    { work_role_id: '', profession_id: '', ensemble_type_id: '' },
  );

  return Object.values(groups)
    .map(str => (str || '').replace(/[+~,]$/, ''))
    .filter(Boolean)
    .join(',');
};

export const getDesiredExpressionParams = expression => {
  const match = expression?.match(/(w\d+:(q|r)\d+)(;|\|)?/);

  if (!match) {
    return {};
  }

  const part = match[0];
  const rest = expression?.slice(part.length);

  return {
    expression: rest,
    desired_expression: match[1],
  };
};

export const getParsedWorkRoles = (expression, tab, CASTING_RESULT_TYPES_VALUES) => {
  if (!expression) {
    return [];
  }

  let targetString = expression;

  if (expression.startsWith('!')) {
    const match = expression.match(/\(([^)]+)\)/);

    if (match && match[1]) {
      targetString = match[1];
    } else {
      return [];
    }
  }

  const rawPairs = targetString.split(/[;|]/).filter(Boolean);

  const workRoles = rawPairs
    .map(pair => {
      const [wPart, rPart] = pair.split(':');

      if (!wPart || !rPart) {
        return null;
      }

      const workId = parseInt(wPart.replace('w', ''), 10);
      const prefix = rPart.charAt(0);
      const roleId = parseInt(rPart.slice(1), 10);

      const roleTypeMap = {
        r: CONTRIBUTORS.CAST,
        q: CONTRIBUTORS.CREW,
        h: CONTRIBUTORS.ENSEMBLE,
      };

      return {
        creditKey: `${wPart}-${rPart}`,
        workId,
        roleId,
        roleType: roleTypeMap[prefix] || 'unknown',
      };
    })
    .filter(Boolean);

  const isDesiredTab = tab === CASTING_RESULT_TYPES_VALUES.DESIRED_ROLE;

  return isDesiredTab && workRoles.length > 1 ? workRoles.slice(1) : workRoles;
};
